import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { money, validityLabel } from "@/lib/api";
import { Empty, ErrorBox, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { PackageForm } from "@/pages/admin/PackageEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Packages() {
  const { data, error, loading } = useFetch("/admin/packages");
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();
  return (
    <div>
      <PageHead eyebrow="Package management" title="Packages" sub="Configure packages, pricing, validity and benefits — no code changes needed"
        actions={<button className="btn-primary" onClick={() => setCreating(true)} data-testid="create-package-button"><Plus size={15} /> New package</button>} />
      {loading && <Spinner />}
      <ErrorBox error={error} />
      {data?.length === 0 && <Empty title="No packages yet" />}
      <div className="surface overflow-x-auto">
        <table className="data-table" data-testid="packages-table">
          <thead><tr><th>Package</th><th>Price</th><th>Validity</th><th>Benefits</th><th>Cards</th><th>Status</th></tr></thead>
          <tbody>{data?.map((p) => (
            <tr key={p.id} className="cursor-pointer" onClick={() => nav(`/admin/packages/${p.id}`)} data-testid={`package-row-${p.slug}`}>
              <td><p className="font-bold text-slate-900">{p.name}</p><p className="font-mono text-[11px] text-slate-400">/{p.slug}</p></td>
              <td className="font-semibold">{money(p.price, p.currency)}</td>
              <td>{validityLabel(p.validity_value, p.validity_unit)}</td>
              <td>{p.benefit_count}</td><td>{p.card_count}</td>
              <td><StatusBadge status={p.status} /></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl">
          <DialogHeader><DialogTitle>New package</DialogTitle></DialogHeader>
          <PackageForm onSaved={(p) => nav(`/admin/packages/${p.id}`)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function BenefitsIndex() {
  const { data, loading } = useFetch("/admin/packages");
  return (
    <div>
      <PageHead eyebrow="Package benefits" title="Benefits by package" sub="Benefits are configurable records attached to each package. Select a package to manage its benefits." />
      {loading && <Spinner />}
      <div className="grid gap-4 md:grid-cols-2">
        {data?.map((p) => (
          <Link key={p.id} to={`/admin/packages/${p.id}#benefits`} className="surface flex items-center justify-between p-5 hover:shadow-md" data-testid={`benefits-package-${p.slug}`}>
            <div><p className="font-bold">{p.name}</p><p className="text-xs text-slate-500">{p.benefit_count} active benefits</p></div>
            <StatusBadge status={p.status} />
          </Link>
        ))}
      </div>
    </div>
  );
}
